import { useState, forwardRef, useImperativeHandle, useEffect } from 'react'
import { motion } from 'framer-motion'
import { FileText, Upload, Link, X, Plus, AlertCircle, Building2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { companyService } from '../../../services/companyService'

const CompanyProfileBuilding = forwardRef(({
  data,
  formData: propFormData,
  onNext,
  onPrevious,
  isLoading,
  userId,
  companyId
}, ref) => {
  // Use either data or formData prop, with proper defaults
  const initialData = propFormData || data || {}

  const [formData, setFormData] = useState({
    industryDomains: initialData.industryDomains || [],
    yearsInOperation: initialData.yearsInOperation || '',
    keyTechnologies: initialData.keyTechnologies || [],
    logo: initialData.logo || null,
    brandAssets: initialData.brandAssets || [],
    websiteUrl: initialData.websiteUrl || '',
    linkedinPage: initialData.linkedinPage || '',
    otherSocialHandles: initialData.otherSocialHandles || []
  })

  const [errors, setErrors] = useState({})
  const [isUploading, setIsUploading] = useState(false)
  const [localLoading, setLocalLoading] = useState(false)
  const [industryOptions, setIndustryOptions] = useState([])
  const [industryLoading, setIndustryLoading] = useState(true)
  const [yearsOfOperationOptions, setYearsOfOperationOptions] = useState([])
  const [yearsOfOperationLoading, setYearsOfOperationLoading] = useState(true)
  const [techStackOptions, setTechStackOptions] = useState([])
  const [techStackLoading, setTechStackLoading] = useState(true)

  // Technology options

  const validateForm = () => {
    const newErrors = {}

    if (formData.industryDomains.length === 0) {
      newErrors.industryDomains = 'Please select at least one industry domain'
    }

    if (!formData.yearsInOperation) {
      newErrors.yearsInOperation = 'Years in operation is required'
    }

    if (formData.keyTechnologies.length === 0) {
      newErrors.keyTechnologies = 'Please select at least one key technology'
    }

    if (formData.websiteUrl && !isValidUrl(formData.websiteUrl)) {
      newErrors.websiteUrl = 'Please enter a valid website URL'
    }

    if (formData.linkedinPage && !isValidLinkedInUrl(formData.linkedinPage)) {
      newErrors.linkedinPage = 'Please enter a valid LinkedIn page URL'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Fetch industry domains on component mount
  useEffect(() => {
    const fetchIndustryDomains = async () => {
      try {
        setIndustryLoading(true)
        const response = await companyService.getEnum('industries')
        if (response && response.items) {
          // Transform API data to match expected format
          // API returns: {type: 'industries', items: [{key: "IT_SERVICES", value: "IT Services"}]}
          setIndustryOptions(response.items)
        } else {
          setIndustryOptions([])
        }
      } catch (error) {
        console.error('Failed to fetch industry domains:', error)
        setIndustryOptions([])
      } finally {
        setIndustryLoading(false)
      }
    }

    fetchIndustryDomains()
  }, [])

  // Fetch years of operation on component mount
  useEffect(() => {
    const fetchYearsOfOperation = async () => {
      try {
        setYearsOfOperationLoading(true)
        const response = await companyService.getEnum('years_in_operation')
        if (response && response.items) {
          // Transform API data to match expected format
          setYearsOfOperationOptions(response.items)
        } else {
          setYearsOfOperationOptions([])
        }
      } catch (error) {
        console.error('Failed to fetch years of operation:', error)
        setYearsOfOperationOptions([])
      } finally {
        setYearsOfOperationLoading(false)
      }
    }

    fetchYearsOfOperation()
  }, [])

  // Fetch tech stacks on component mount
  useEffect(() => {
    const fetchTechStacks = async () => {
      try {
        setTechStackLoading(true)
        const response = await companyService.getEnum('technologies')
        if (response && response.items) {
          // Transform API data to match expected format
          // API returns: {type: 'technologies', items: [{key: "PYTHON", value: "Python"}]}
          setTechStackOptions(response.items)
        } else {
          setTechStackOptions([])
        }
      } catch (error) {
        console.error('Failed to fetch tech stacks:', error)
        setTechStackOptions([])
      } finally {
        setTechStackLoading(false)
      }
    }

    fetchTechStacks()
  }, [])

  const isValidUrl = (url) => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  const isValidLinkedInUrl = (url) => {
    return url.includes('linkedin.com/company/') || url.includes('linkedin.com/in/')
  }

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }))
    }
  }

  const handleIndustryDomainToggle = (domain) => {
    setFormData(prev => ({
      ...prev,
      industryDomains: prev.industryDomains.includes(domain)
        ? prev.industryDomains.filter(d => d !== domain)
        : [...prev.industryDomains, domain]
    }))
  }

  const handleSelectAllIndustries = () => {
    setFormData(prev => ({
      ...prev,
      industryDomains: industryOptions.map(o => o.key)
    }))
  }

  const handleDeselectAllIndustries = () => {
    setFormData(prev => ({
      ...prev,
      industryDomains: []
    }))
  }

  const handleTechnologyToggle = (tech) => {
    setFormData(prev => ({
      ...prev,
      keyTechnologies: prev.keyTechnologies.includes(tech)
        ? prev.keyTechnologies.filter(t => t !== tech)
        : [...prev.keyTechnologies, tech]
    }))
  }

  const handleSelectAllTechnologies = () => {
    setFormData(prev => ({
      ...prev,
      keyTechnologies: techStackOptions.map(o => o.key)
    }))
  }

  const handleDeselectAllTechnologies = () => {
    setFormData(prev => ({
      ...prev,
      keyTechnologies: []
    }))
  }

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    // Validate file
    const maxSize = 5 * 1024 * 1024 // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/svg+xml']

    if (file.size > maxSize) {
      toast.error('Logo file size must be less than 5MB')
      return
    }

    if (!allowedTypes.includes(file.type)) {
      toast.error('Logo must be a JPG, PNG, or SVG file')
      return
    }

    setIsUploading(true)
    try {
      // Simulate file upload - replace with actual upload logic
      await new Promise(resolve => setTimeout(resolve, 2000))

      // In a real implementation, you would upload the file and get a URL
      const logoUrl = await uploadFileToServer(file) // Implement this function

      setFormData(prev => ({
        ...prev,
        logo: {
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          url: logoUrl // Use the actual uploaded URL
        }
      }))

      toast.success('Logo uploaded successfully!')
    } catch (error) {
      toast.error('Failed to upload logo. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleBrandAssetsUpload = async (event) => {
    const files = Array.from(event.target.files)
    if (files.length === 0) return

    setIsUploading(true)
    try {
      // Simulate file upload - replace with actual upload logic
      await new Promise(resolve => setTimeout(resolve, 2000))

      // In a real implementation, you would upload files and get URLs
      const uploadedAssets = await Promise.all(
        files.map(async (file) => {
          const assetUrl = await uploadFileToServer(file) // Implement this function
          return {
            file,
            name: file.name,
            size: file.size,
            type: file.type,
            url: assetUrl
          }
        })
      )

      setFormData(prev => ({
        ...prev,
        brandAssets: [...prev.brandAssets, ...uploadedAssets]
      }))

      toast.success(`${files.length} file(s) uploaded successfully!`)
    } catch (error) {
      toast.error('Failed to upload files. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  // Mock file upload function - replace with actual implementation
  const uploadFileToServer = async (file) => {
    // Implement your actual file upload logic here
    // This should return the URL of the uploaded file
    return `https://example.com/uploads/${file.name}`
  }

  const removeBrandAsset = (index) => {
    setFormData(prev => ({
      ...prev,
      brandAssets: prev.brandAssets.filter((_, i) => i !== index)
    }))
  }

  const addSocialHandle = () => {
    setFormData(prev => ({
      ...prev,
      otherSocialHandles: [...prev.otherSocialHandles, { platform: '', url: '' }]
    }))
  }

  const updateSocialHandle = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      otherSocialHandles: prev.otherSocialHandles.map((handle, i) =>
        i === index ? { ...handle, [field]: value } : handle
      )
    }))
  }

  const removeSocialHandle = (index) => {
    setFormData(prev => ({
      ...prev,
      otherSocialHandles: prev.otherSocialHandles.filter((_, i) => i !== index)
    }))
  }

  // Helper function to parse years in operation to number
  const parseYearsInOperation = (yearsKey) => {
    if (!yearsKey) return 0

    // Map API Enum Keys to Integer values expected by backend
    const yearsMap = {
      // Old formats (just in case)
      '0-1': 0,
      '1-3': 1,
      '3-5': 3,
      '5-10': 5,
      '10-15': 10,
      '15+': 15,

      // New Enum Keys from API
      'LESS_THAN_1': 0,
      'FROM_1_TO_3': 1, // Common convention
      '1_TO_3': 1,      // Alternative convention
      'FROM_3_TO_5': 3,
      '3_TO_5': 3,
      'FROM_5_TO_10': 5,
      '5_TO_10': 5,
      'FROM_10_TO_15': 10,
      '10_TO_15': 10,
      'MORE_THAN_15': 15,
      '15_PLUS': 15
    }

    if (yearsMap[yearsKey] !== undefined) return yearsMap[yearsKey]

    // Fallback: try to extract first number
    const match = yearsKey.toString().match(/\d+/)
    if (match) {
      // Special case for 'LESS_THAN_1' if regex catches 1 but we want 0?
      // But map should catch it.
      return parseInt(match[0])
    }

    return 0
  }

  // Expose submit function via ref
  useImperativeHandle(ref, () => ({
    submitForm: () => {
      console.log('CompanyProfileBuilding: submitForm called via ref')
      handleStepComplete()
    }
  }))

  const handleStepComplete = async () => {
    console.log('CompanyProfileBuilding: handleStepComplete called')

    // Check if user ID is available
    if (!userId) {
      console.error('CompanyProfileBuilding: No user ID provided')
      toast.error('User not authenticated. Please log in again.')
      return
    }

    if (validateForm()) {
      console.log('Form validation passed, calling onNext with data:', formData)

      try {
        setLocalLoading(true)

        // Prepare data according to backend structure (matching vendor-profile API)
        const dataToSave = {
          company_id: Number(companyId) || 0, // Ensure valid number
          user_id: Number(userId) || 0,       // Ensure valid number
          industry_domains: formData.industryDomains,
          years_in_operation: parseYearsInOperation(formData.yearsInOperation),
          key_technologies: formData.keyTechnologies,
          website_url: formData.websiteUrl || "",
          linkedin_url: formData.linkedinPage || "",
          other_social_handles: formData.otherSocialHandles.length > 0
            ? formData.otherSocialHandles.map(handle =>
              `${handle.platform}: ${handle.url}`
            ).join(' | ')
            : '',
          logo_url: formData.logo?.url || "",
          brand_assets_url: formData.brandAssets.length > 0
            ? formData.brandAssets.map(asset => asset.url).join(',')
            : ""
        }

        console.log('Saving company profile data with user_id:', userId, 'and company_id:', companyId)
        console.log('Data to save:', dataToSave)

        await onNext(dataToSave)
        console.log('onNext completed successfully')
      } catch (error) {
        console.error('Error in handleStepComplete:', error)
        const errorMessage = error.message || 'Failed to save company profile. Please try again.'
        toast.error(errorMessage)
        throw error // Re-throw to let parent handle it
      } finally {
        setLocalLoading(false)
      }
    } else {
      console.log('Form validation failed')
      toast.error('Please fix the errors before continuing.')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    console.log('CompanyProfileBuilding: form submitted via form element')

    // Check if user ID is available
    if (!userId) {
      toast.error('User not authenticated. Please log in again.')
      return
    }

    handleStepComplete()
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8" noValidate>
      {/* Hidden submit button for form submission */}
      <button type="submit" className="hidden">Submit</button>

      {/* User ID and Company ID Display (for debugging, remove in production) */}
      {/* {(userId || companyId) && (
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
          <p className="text-sm text-blue-700">
            <strong>User ID:</strong> {userId}
          </p>
          {companyId && (
            <p className="text-sm text-blue-700 mt-1">
              <strong>Company ID:</strong> {companyId}
            </p>
          )}
        </div>
      )} */}

      {/* Industry Domains */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-white">Industry Domains Served *</h3>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={handleSelectAllIndustries}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Select All
            </button>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              onClick={handleDeselectAllIndustries}
              className="text-sm text-gray-600 hover:text-gray-700 font-medium"
            >
              Deselect All
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-3">
          {industryLoading ? (
            <div className="col-span-full text-center py-4">
              <div className="inline-flex items-center text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2"></div>
                Loading industry domains...
              </div>
            </div>
          ) : industryOptions.length === 0 ? (
            <div className="col-span-full text-center py-4 text-gray-500">
              No industry domains available
            </div>
          ) : (
            industryOptions.map((domain) => (
              <label key={domain.key} className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.industryDomains.includes(domain.key)}
                  onChange={() => handleIndustryDomainToggle(domain.key)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                />
                <span className="ml-2 text-sm text-secondary-700 dark:text-secondary-300">{domain.value}</span>
              </label>
            ))
          )}
        </div>
        {errors.industryDomains && (
          <p className="text-error-500 text-sm flex items-center">
            <AlertCircle className="w-4 h-4 mr-1" />
            {errors.industryDomains}
          </p>
        )}
      </div>

      {/* Years in Operation */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-blue-900 dark:text-white">Years in Operation *</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="label block mb-2">Years in Operation</label>
            <select
              value={formData.yearsInOperation}
              onChange={(e) => handleChange('yearsInOperation', e.target.value)}
              className="input"
              required
              disabled={yearsOfOperationLoading}
            >
              {yearsOfOperationLoading ? (
                <option>Loading...</option>
              ) : yearsOfOperationOptions.length === 0 ? (
                <option disabled>No options available</option>
              ) : (
                <>
                  <option value="">Select years</option>
                  {yearsOfOperationOptions.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.value}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
        </div>
        {errors.yearsInOperation && (
          <p className="text-error-500 text-sm flex items-center">
            <AlertCircle className="w-4 h-4 mr-1" />
            {errors.yearsInOperation}
          </p>
        )}
      </div>

      {/* Key Technologies */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-white">Key Technologies Offered *</h3>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={handleSelectAllTechnologies}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Select All
            </button>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              onClick={handleDeselectAllTechnologies}
              className="text-sm text-gray-600 hover:text-gray-700 font-medium"
            >
              Deselect All
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {techStackLoading ? (
            <div className="col-span-full text-center py-4">
              <div className="inline-flex items-center text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2"></div>
                Loading technologies...
              </div>
            </div>
          ) : techStackOptions.length === 0 ? (
            <div className="col-span-full text-center py-4 text-gray-500">
              No technologies available
            </div>
          ) : (
            techStackOptions.map((tech) => (
              <label key={tech.key} className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.keyTechnologies.includes(tech.key)}
                  onChange={() => handleTechnologyToggle(tech.key)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                />
                <span className="ml-2 text-sm text-secondary-700 dark:text-secondary-300">{tech.value}</span>
              </label>
            ))
          )}
        </div>
        {errors.keyTechnologies && (
          <p className="text-error-500 text-sm flex items-center">
            <AlertCircle className="w-4 h-4 mr-1" />
            {errors.keyTechnologies}
          </p>
        )}
      </div>

      {/* Logo & Brand Assets - 2 Columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Logo Upload */}
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-white flex items-center">
            <Upload className="w-5 h-5 mr-2 text-blue-600 dark:text-primary-600" />
            Company Logo
          </h3>

          <div className="space-y-4">
            {formData.logo ? (
              <div className="flex items-center space-x-4">
                <img
                  src={formData.logo.url}
                  alt="Company logo"
                  className="w-20 h-20 object-contain border border-secondary-200 rounded-lg"
                />
                <div>
                  <p className="font-medium text-secondary-900">{formData.logo.name}</p>
                  <p className="text-sm text-secondary-500">
                    {(formData.logo.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-secondary-300 rounded-lg p-6 text-center h-full flex flex-col justify-center">
                <Upload className="w-8 h-8 text-secondary-400 mx-auto mb-2" />
                <p className="text-secondary-600 mb-2">Upload your company logo</p>
                <p className="text-sm text-secondary-500 mb-4">
                  Max size: 5MB | Supported formats: JPG, PNG, SVG
                </p>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/svg+xml"
                  onChange={handleLogoUpload}
                  disabled={isUploading}
                  className="hidden"
                  id="logo-upload"
                />
                <label
                  htmlFor="logo-upload"
                  className="btn btn-outline cursor-pointer"
                >
                  {isUploading ? 'Uploading...' : 'Choose Logo'}
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Brand Assets */}
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-white flex items-center">
            <FileText className="w-5 h-5 mr-2 text-blue-600 dark:text-primary-600" />
            Brand Assets
          </h3>

          <div className="space-y-4">
            {formData.brandAssets.length > 0 && (
              <div className="space-y-2">
                {formData.brandAssets.map((asset, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-secondary-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FileText className="w-5 h-5 text-secondary-400" />
                      <div>
                        <p className="font-medium text-secondary-900 truncate max-w-[150px]">{asset.name}</p>
                        <p className="text-sm text-secondary-500">
                          {(asset.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeBrandAsset(index)}
                      className="text-error-500 hover:text-error-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="border-2 border-dashed border-secondary-300 rounded-lg p-6 text-center h-full flex flex-col justify-center min-h-[180px]">
              <Upload className="w-8 h-8 text-secondary-400 mx-auto mb-2" />
              <p className="text-secondary-600 mb-2">Upload brand assets</p>
              <p className="text-sm text-secondary-500 mb-4">
                ZIP files or multiple files supported
              </p>
              <input
                type="file"
                multiple
                onChange={handleBrandAssetsUpload}
                disabled={isUploading}
                className="hidden"
                id="brand-assets-upload"
              />
              <label
                htmlFor="brand-assets-upload"
                className="btn btn-outline cursor-pointer"
              >
                {isUploading ? 'Uploading...' : 'Choose Files'}
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Website & Social Media */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-blue-900 dark:text-white flex items-center">
          <Link className="w-5 h-5 mr-2 text-blue-600 dark:text-primary-600" />
          Website & Social Media
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="label block mb-2">Website URL</label>
            <input
              type="url"
              value={formData.websiteUrl}
              onChange={(e) => handleChange('websiteUrl', e.target.value)}
              className="input"
              placeholder="https://www.yourcompany.com"
            />
            {errors.websiteUrl && (
              <p className="text-error-500 text-sm mt-1 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.websiteUrl}
              </p>
            )}
          </div>

          <div>
            <label className="label block mb-2">LinkedIn Page</label>
            <input
              type="url"
              value={formData.linkedinPage}
              onChange={(e) => handleChange('linkedinPage', e.target.value)}
              className="input"
              placeholder="https://linkedin.com/company/yourcompany"
            />
            {errors.linkedinPage && (
              <p className="text-error-500 text-sm mt-1 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.linkedinPage}
              </p>
            )}
          </div>
        </div>

        {/* Other Social Handles */}
        <div className="space-y-4">
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={addSocialHandle}
              className="btn btn-outline btn-sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Handle
            </button>
          </div>

          {formData.otherSocialHandles.map((handle, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="text"
                value={handle.platform}
                onChange={(e) => updateSocialHandle(index, 'platform', e.target.value)}
                className="input"
                placeholder="Platform (e.g., Twitter)"
              />
              <input
                type="url"
                value={handle.url}
                onChange={(e) => updateSocialHandle(index, 'url', e.target.value)}
                className="input"
                placeholder="URL"
              />
              <button
                type="button"
                onClick={() => removeSocialHandle(index)}
                className="btn btn-outline text-error-500 hover:text-error-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>


    </form>
  )
})

export default CompanyProfileBuilding